/* Connected-mailbox client. Talks to /api/email when reachable (real Gmail /
 * Microsoft OAuth + send); falls back to a localStorage mailbox + outbox so the
 * standalone demo can link a mailbox and "send" end to end. */

export type MailProvider = "gmail" | "microsoft";
export interface Mailbox { provider: MailProvider; email: string; connectedAt: string; simulated: boolean; }
export interface OutboxEmail { id: string; from: string; to: string; subject: string; body: string; status: "sent" | "failed"; simulated: boolean; at: string; accountId?: string; }
export interface Providers { gmail: { configured: boolean }; microsoft: { configured: boolean }; }
export type Mode = "api" | "local";

const API = (import.meta.env.VITE_API_URL as string | undefined) || "/api";
const LS_MB = "penumbrai:mailbox";
const LS_OUT = "penumbrai:outbox";

async function req<T>(path: string, opts?: RequestInit, timeoutMs = 1800): Promise<T | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(`${API}${path}`, { credentials: "include", headers: { "content-type": "application/json" }, signal: ctrl.signal, ...opts });
    clearTimeout(t);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch { return null; }
}
const readLS = <T,>(k: string, d: T): T => { try { const r = localStorage.getItem(k); if (r) return JSON.parse(r) as T; } catch { /* ignore */ } return d; };
const writeLS = (k: string, v: unknown) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };
const uid = () => `out_${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;

/** Load mailbox status + which providers offer real OAuth, deciding the mode. */
export async function loadMailbox(): Promise<{ mode: Mode; mailbox: Mailbox | null; providers: Providers }> {
  const [mb, providers] = await Promise.all([req<{ mailbox: Mailbox | null }>("/email/mailbox"), req<Providers>("/email/providers")]);
  if (mb && providers) return { mode: "api", mailbox: mb.mailbox, providers };
  return { mode: "local", mailbox: readLS<Mailbox | null>(LS_MB, null), providers: { gmail: { configured: false }, microsoft: { configured: false } } };
}

/** Connect a mailbox. In API mode redirect to the provider (or the server's
 * simulate); in local mode link a simulated mailbox for the given address. */
export function connect(mode: Mode, provider: MailProvider, meEmail: string, redirect = "/settings"): Mailbox | null {
  if (mode === "api") { window.location.href = `${API}/email/connect/${provider}/start?redirect=${encodeURIComponent(redirect)}`; return null; }
  const mailbox: Mailbox = { provider, email: meEmail, connectedAt: new Date().toISOString(), simulated: true };
  writeLS(LS_MB, mailbox);
  return mailbox;
}
export async function disconnect(mode: Mode): Promise<void> {
  if (mode === "api") { await req("/email/disconnect", { method: "POST" }); return; }
  writeLS(LS_MB, null);
}
export async function sendEmail(mode: Mode, msg: { to: string; subject: string; body: string; accountId?: string }): Promise<OutboxEmail> {
  if (mode === "api") { const e = await req<OutboxEmail>("/email/send", { method: "POST", body: JSON.stringify(msg) }); if (e) return e; }
  const mb = readLS<Mailbox | null>(LS_MB, null);
  const entry: OutboxEmail = { id: uid(), from: mb?.email ?? "you@company.com", to: msg.to, subject: msg.subject, body: msg.body, status: "sent", simulated: true, at: new Date().toISOString(), accountId: msg.accountId };
  writeLS(LS_OUT, [entry, ...readLS<OutboxEmail[]>(LS_OUT, [])]);
  return entry;
}
export async function getOutbox(mode: Mode): Promise<OutboxEmail[]> {
  if (mode === "api") { const d = await req<{ emails: OutboxEmail[] }>("/email/outbox"); if (d) return d.emails; }
  return readLS<OutboxEmail[]>(LS_OUT, []);
}
